import { useCallback, useEffect, useRef, useState } from "react"

type Options = {
  /** Where the chosen width is remembered, so it outlives the tab. */
  storageKey: string
  /** Width before anyone has dragged it — the figure the canvas draws. */
  initial: number
  min: number
  max: number
  /**
   * A second ceiling, as a share of the window. Without it a width chosen on a
   * wide display keeps every pixel on a narrow one, which is how the rail came
   * to take a third of a small screen while the panes beside it gave way.
   */
  maxShare: number
  /**
   * Drag below this and the column closes rather than fighting the floor.
   *
   * A minimum on its own means the last part of the drag does nothing, which
   * reads as the handle being stuck. Past this point the intent is plainly "get
   * it out of the way", so it goes — and dragging back out past the same point
   * brings it to `min`, which is where it can be read again.
   */
  collapseAt?: number
}

function readStored(key: string, fallback: number) {
  try {
    const raw = window.localStorage.getItem(key)
    const parsed = raw === null ? Number.NaN : Number.parseFloat(raw)
    return Number.isFinite(parsed) ? parsed : fallback
  } catch {
    // Private mode, or storage disabled. The default is a fine answer.
    return fallback
  }
}

/**
 * A pixel width the reader can drag, bounded and remembered.
 *
 * `react-resizable-panels` would be the obvious home for this, but it sizes in
 * percentages, and a percentage cannot express "no narrower than 200px" without
 * being recomputed from the group's width on every resize — at which point the
 * library re-validates each panel against constraints its siblings have not
 * updated yet, and the clamp quietly does nothing. A column whose real
 * constraint is pixels is better off owning those pixels.
 *
 * Returns the width to render and the props for the handle that changes it.
 */
export function useResizableWidth({
  storageKey,
  initial,
  min,
  max,
  maxShare,
  collapseAt,
}: Options) {
  const [chosen, setChosen] = useState(() => readStored(storageKey, initial))
  const [dragging, setDragging] = useState(false)
  const [windowWidth, setWindowWidth] = useState(() =>
    typeof window === "undefined" ? 0 : window.innerWidth,
  )
  const origin = useRef({ x: 0, width: 0 })

  useEffect(() => {
    const onResize = () => setWindowWidth(window.innerWidth)
    window.addEventListener("resize", onResize)
    return () => window.removeEventListener("resize", onResize)
  }, [])

  useEffect(() => {
    try {
      window.localStorage.setItem(storageKey, String(chosen))
    } catch {
      // Nothing to do; the width simply will not survive the session.
    }
  }, [chosen, storageKey])

  /*
    A drag over a page full of text selects that text, and the cursor reverts to
    whatever is under the pointer the moment it leaves the handle. Both belong to
    the document rather than to either element, so they are set here and cleaned
    up when the drag ends.
  */
  useEffect(() => {
    if (!dragging) return
    const { body } = document
    const previousCursor = body.style.cursor
    const previousSelect = body.style.userSelect
    body.style.cursor = "col-resize"
    body.style.userSelect = "none"
    return () => {
      body.style.cursor = previousCursor
      body.style.userSelect = previousSelect
    }
  }, [dragging])

  // The ceiling can fall below the floor on a very narrow window; the floor wins,
  // because a rail too small to read is worse than one that is slightly too wide.
  const ceiling = windowWidth > 0 ? Math.min(max, windowWidth * maxShare) : max
  const collapsed = collapseAt !== undefined && chosen < collapseAt
  const width = collapsed ? 0 : Math.round(Math.max(min, Math.min(chosen, ceiling)))

  const onPointerDown = useCallback(
    (event: React.PointerEvent<HTMLElement>) => {
      event.preventDefault()
      origin.current = { x: event.clientX, width }
      setDragging(true)
      event.currentTarget.setPointerCapture(event.pointerId)
    },
    [width],
  )

  const onPointerMove = useCallback(
    (event: React.PointerEvent<HTMLElement>) => {
      if (!dragging) return
      setChosen(origin.current.width + (event.clientX - origin.current.x))
    },
    [dragging],
  )

  const endDrag = useCallback(
    (event: React.PointerEvent<HTMLElement>) => {
      if (!dragging) return
      setDragging(false)
      if (event.currentTarget.hasPointerCapture(event.pointerId)) {
        event.currentTarget.releasePointerCapture(event.pointerId)
      }
    },
    [dragging],
  )

  const setCollapsed = useCallback(
    (next: boolean) => setChosen(next ? 0 : initial),
    [initial],
  )

  // A drag handle that only answers to a pointer is a handle some people cannot
  // use, so the arrows move it too — the same chord the panel handles offer.
  const onKeyDown = useCallback((event: React.KeyboardEvent<HTMLElement>) => {
    const step = event.shiftKey ? 40 : 10
    if (event.key === "ArrowLeft") {
      event.preventDefault()
      setChosen((current) => current - step)
    } else if (event.key === "ArrowRight") {
      event.preventDefault()
      setChosen((current) => current + step)
    }
  }, [])

  return {
    width,
    dragging,
    collapsed,
    /** For the control on the seam, and for a double-click on the seam itself. */
    toggle: () => setCollapsed(!collapsed),
    handleProps: {
      role: "separator" as const,
      "aria-orientation": "vertical" as const,
      "aria-valuenow": width,
      "aria-valuemin": collapseAt ?? min,
      "aria-valuemax": Math.round(ceiling),
      tabIndex: 0,
      onPointerDown,
      onPointerMove,
      onPointerUp: endDrag,
      onPointerCancel: endDrag,
      onKeyDown,
    },
  }
}
